<!-- Routed create page (roles/create) — styled with the Phase 3 editor
     template (this form is the Role editor's twin and its access matrices
     are too large for a modal; the create-modal template applies to the
     small create forms). -->

<template>
  <div class="bui-entity-listing-page create-role-redesign">
    <div class="create-role-redesign__inner">
      <!-- Page header -->
      <header class="pe-header">
        <div class="pe-header__left">
          <div class="pe-header__title-row">
            <h1 class="bui-page-title">Create Role</h1>
          </div>
          <div class="pe-header__subtitle">{{ values.name }}</div>
        </div>
      </header>

      <!-- Two-column editor grid -->
      <section class="editor-grid">
        <!-- LEFT COLUMN -->
        <div class="editor-col">
          <!-- Role Details -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Role Details</h2>
            </template>

            <StatusBar
              :errorMessage="errorMessage"
              :isLoading="isLoading"
              loadingMessage="Saving..."
              :isSuccess="isSuccess"
              successMessage="Saved!"
            />

            <div class="field-content">
              <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
              <div class="d-flex field-row">
                <label for="cr-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
                <TextField id="cr-name" v-model="values.name" class="basis-1/2" width="100%" />
              </div>
            </div>
          </InfoCard>

          <!-- Application Areas -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Application Areas</h2>
            </template>
            <template #actions>
              <button type="button" class="ue-link" @click="grantAllArea()">Grant All</button>
              <button type="button" class="ue-link" @click="revokeAllArea()">Revoke All</button>
            </template>

            <div class="access-matrix">
              <div
                class="access-matrix__row"
                v-for="item of createModel?.commonEntity.roleAreaAccesses"
                :key="item.area.name"
              >
                <span class="access-matrix__name">{{ item.area.name }}</span>
                <div class="access-matrix__levels">
                  <label
                    v-for="level of accessLevels"
                    :key="level.name"
                    class="access-pill"
                    :class="{
                      active: level.value > 0 && item.access >= level.value,
                      selected: item.access === level.value,
                    }"
                  >
                    <input
                      type="radio"
                      :name="'area-' + item.area.name"
                      v-model="item.access"
                      :value="level.value"
                      @change="hasChanges = true"
                    />
                    {{ level.name }}
                  </label>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="editor-col">
          <!-- Media Types -->
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Media Types</h2>
            </template>
            <template #actions>
              <button type="button" class="ue-link" @click="grantAllAsset()">Grant All</button>
              <button type="button" class="ue-link" @click="revokeAllAsset()">Revoke All</button>
            </template>

            <div class="access-matrix">
              <div
                class="access-matrix__row"
                v-for="item of createModel?.commonEntity.roleAssetTypeAccesses"
                :key="item.assetType"
              >
                <span class="access-matrix__name">{{ findAssetById(item.assetType) }}</span>
                <div class="access-matrix__levels">
                  <label
                    v-for="level of accessLevels"
                    :key="level.name"
                    class="access-pill"
                    :class="{
                      active: level.value > 0 && item.access >= level.value,
                      selected: item.access === level.value,
                    }"
                  >
                    <input
                      type="radio"
                      :name="'asset-' + item.assetType"
                      v-model="item.access"
                      :value="level.value"
                      @change="hasChanges = true"
                    />
                    {{ level.name }}
                  </label>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </section>

      <!-- Sticky footer -->
      <footer class="editor-footer">
        <div class="editor-footer__inner">
          <ButtonCustomized label="Exit" v-if="!hasChanges" @click="onExit" ctaSecondary />
          <BeforeExitDialogue v-if="hasChanges" @save="submitAndExit" @exit="onExit" />
          <ButtonCustomized label="Save" cta @click="onSubmit" :disabled="!hasChanges" />
          <Saving ref="savingDialog" />
        </div>
      </footer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import TextField from "@/components/form-fields/TextField.vue";
import useCreateRoleForm from "./CreateRoleHelper";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import useNotifications from "@/components/Notifications/composables/notifications";
import router from '@/router';
import { onBeforeMount, ref } from "vue";
import BeforeExitDialogue from '@/components/Dialog/BeforeExitDialog/BeforeExitDialogue.vue';
import Saving from '@/components/Dialog/SavingDialog/Saving.vue'
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';

const notification = useNotifications();
const savingDialog = ref()
const { values, submitForm, isLoading, isSuccess, errorMessage, accessLevels,
  newRoleId, hasChanges, removeBeforeLoad, getRoleModel
  , createModel, findAssetById } = useCreateRoleForm();
//getRoleModel();
onBeforeMount(async () => {
  await getRoleModel();

})

async function onSubmit() {
  await submitForm();
  if (isSuccess.value === true) {
    hasChanges.value = false
    notification.success('Role created successfully!');
    router.push({ name: 'role-editor', params: { id: newRoleId.value } });
  }
}
async function submitAndExit() {
  savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    notification.success('Role created successfully!');
    removeBeforeLoad();
    router.push({ name: 'role-editor', params: { id: newRoleId.value } });
  }
}

function onExit() {
  removeBeforeLoad()
  router.push({ name: 'roles' });
}
function grantAllArea() {
  createModel.value.commonEntity.roleAreaAccesses.forEach((item: any) => {
    item.access = accessLevels[4].value
  })
}
function revokeAllArea() {
  createModel.value.commonEntity.roleAreaAccesses.forEach((item: any) => {
    item.access = accessLevels[0].value
  })
}
function grantAllAsset() {
  createModel.value.commonEntity.roleAssetTypeAccesses.forEach((item: any) => {
    item.access = accessLevels[4].value
  })
}
function revokeAllAsset() {
  createModel.value.commonEntity.roleAssetTypeAccesses.forEach((item: any) => {
    item.access = accessLevels[0].value
  })
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.create-role-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.create-role-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* ---- Access matrix (same treatment as RoleEditorView) ---- */
.access-matrix {
  display: flex;
  flex-direction: column;
}

.access-matrix__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--bui-tbl-line, #ffd9e6);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.access-matrix__name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2a30);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.access-matrix__levels {
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
}

.access-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--bui-badge-radius, 100px);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--bui-tbl-detail-text, #5c5c66);
  background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;

  /* The radio stays for a11y/keyboard but the pill is the visual control. */
  input[type='radio'] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &:has(input:focus-visible) {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 1px;
  }

  &:hover {
    background: var(--bui-tbl-rowbtn-hover-fill, #ececf0);
  }

  &.active {
    background: var(--bui-badge-tag-fill, #ffd9e6);
    color: var(--bui-badge-tag-text, #a420c8);
  }

  &.selected {
    background: var(--bui-chk-checked-fill, #de3da9);
    color: var(--bui-chk-fill, #fffcfd);
  }
}
</style>
